import { Link } from "react-router-dom";
import { CalendarDays, Users, Camera, Megaphone, Plus, Pencil, ArrowRight } from "lucide-react";
import { useResource, SectionHeader, Loading } from "./components";

const fmt = (iso) => {
  if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return iso || "";
  return new Date(iso.slice(0, 10) + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
};

export default function Dashboard() {
  const games = useResource("games");
  const players = useResource("players");
  const news = useResource("news");
  const gallery = useResource("gallery");

  if (games.loading || players.loading || news.loading || gallery.loading) return <Loading />;

  const sorted = [...games.items].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  const upcoming = sorted.filter((g) => g.wl === "TBD" || !g.wl);
  const results = sorted.filter((g) => g.wl === "W" || g.wl === "L").reverse();

  const stats = [
    { label: "Games Scheduled", value: games.items.length, to: "/admin/games", testid: "dash-stat-games" },
    { label: "Players", value: players.items.length, to: "/admin/players", testid: "dash-stat-players" },
    { label: "Photos", value: gallery.items.length, to: "/admin/photos", testid: "dash-stat-photos" },
    { label: "Announcements", value: news.items.length, to: "/admin/news", testid: "dash-stat-news" },
  ];

  const actions = [
    { to: "/admin/games", icon: Plus, title: "Add a Game", text: "Put a new game on the schedule", testid: "dash-action-add-game" },
    { to: "/admin/news", icon: Megaphone, title: "Post an Announcement", text: "Share news with parents & fans", testid: "dash-action-add-news" },
    { to: "/admin/photos", icon: Camera, title: "Upload Photos", text: "Add game or event pictures", testid: "dash-action-add-photos" },
    { to: "/admin/players", icon: Users, title: "Update Roster", text: "Add or edit a player", testid: "dash-action-roster" },
  ];

  return (
    <div data-testid="admin-dashboard-home">
      <SectionHeader title="Dashboard" subtitle="Welcome back. Everything on the website can be updated from here — no coding needed." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats.map((s) => (
          <Link key={s.label} to={s.to} data-testid={s.testid} className="card-lift bg-white rounded-lg border border-slate-200 p-5 hover:border-[#0F4C2A]/40">
            <div className="font-stat text-3xl font-bold text-[#0F4C2A]">{s.value}</div>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mt-1">{s.label}</div>
          </Link>
        ))}
      </div>

      <h2 className="font-condensed text-2xl font-bold uppercase text-slate-900 mb-4">Quick Actions</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
        {actions.map((a) => (
          <Link key={a.title} to={a.to} data-testid={a.testid} className="card-lift flex items-center gap-4 bg-[#0F4C2A] text-white rounded-lg p-5 hover:bg-[#0A3823]">
            <div className="h-12 w-12 rounded-md bg-white/15 flex items-center justify-center shrink-0">
              <a.icon size={22} className="text-[#F59E0B]" />
            </div>
            <div>
              <div className="font-condensed text-xl font-bold uppercase">{a.title}</div>
              <div className="text-xs text-white/75">{a.text}</div>
            </div>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid="dash-upcoming-games">
          <h3 className="font-condensed text-xl font-bold uppercase text-[#0F4C2A] mb-4 flex items-center gap-2"><CalendarDays size={18} /> Upcoming Games</h3>
          {upcoming.length === 0 && <p className="text-sm text-slate-500">No upcoming games. Add one from Games & Schedule.</p>}
          {upcoming.slice(0, 5).map((g) => (
            <Link to="/admin/games" key={g.id} className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0 hover:bg-slate-50 -mx-2 px-2 rounded" data-testid={`dash-upcoming-${g.id}`}>
              <div>
                <p className="text-sm font-bold text-slate-900">{g.ha === "Away" ? "at" : "vs"} {g.opponent}</p>
                <p className="text-xs text-slate-500">{fmt(g.date)} · {g.time}</p>
              </div>
              <ArrowRight size={15} className="text-slate-400" />
            </Link>
          ))}
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid="dash-recent-results">
          <h3 className="font-condensed text-xl font-bold uppercase text-[#0F4C2A] mb-4 flex items-center gap-2"><Pencil size={18} /> Recent Results</h3>
          {results.length === 0 && <p className="text-sm text-slate-500">No results yet this season.</p>}
          {results.slice(0, 5).map((g) => (
            <div key={g.id} className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0" data-testid={`dash-result-${g.id}`}>
              <div>
                <p className="text-sm font-bold text-slate-900">{g.ha === "Away" ? "at" : "vs"} {g.opponent}</p>
                <p className="text-xs text-slate-500">{fmt(g.date)}</p>
              </div>
              <span className={`font-stat font-bold text-xs px-2.5 py-1 rounded-sm ${g.wl === "W" ? "bg-green-100 text-[#166534]" : "bg-red-100 text-[#991B1B]"}`}>
                {g.wl} {g.score}
              </span>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid="dash-recent-news">
          <h3 className="font-condensed text-xl font-bold uppercase text-[#0F4C2A] mb-4 flex items-center gap-2"><Megaphone size={18} /> Recent Announcements</h3>
          {news.items.length === 0 && <p className="text-sm text-slate-500">No announcements yet.</p>}
          {news.items.slice(0, 3).map((n) => (
            <Link to="/admin/news" key={n.id} className="block py-2.5 border-b border-slate-100 last:border-0 hover:bg-slate-50 -mx-2 px-2 rounded" data-testid={`dash-news-${n.id}`}>
              <p className="text-sm font-bold text-slate-900 leading-snug">{n.title}</p>
              <p className="text-xs text-slate-500 mt-0.5">{n.date}</p>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
